import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { getStoredUser, isRefreshTokenValid } from '../../services/keycloakAuthService';
import { ENV } from '../../config/env';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if auth guard is enabled via environment variable
  const authGuardEnabled = ENV.AUTH_GUARD;

  useEffect(() => {
    // If auth guard is disabled, skip authentication check
    if (!authGuardEnabled) {
      setIsChecking(false);
      setIsAuthenticated(true);
      return;
    }

    // Check authentication status asynchronously
    const checkAuth = async () => {
      try {
        const user = await getStoredUser();
        const hasValidToken = await isRefreshTokenValid();
        setIsAuthenticated(!!user && hasValidToken);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    void checkAuth();
  }, [authGuardEnabled]);

  // Show loading state while checking authentication
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If auth guard is disabled, allow access to all pages (development mode)
  if (!authGuardEnabled) {
    return <>{children}</>;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // User is authenticated, allow access
  return <>{children}</>;
};
